import { Panel } from '../core/Panel';
import { Sim } from '../../game/simulation';
import { Settings, GraphicsPreset } from '../Settings';
import { Save } from '../../save/save';

/**
 * Settings — centered modal with three sections.
 *
 *   SOUND     Master volume slider + Mute toggle. Applies to every
 *             one-shot SFX, ambient bed, and positional emitter
 *             (they all route through SoundBank's master bus).
 *
 *   GRAPHICS  Preset chips at top (Low / Medium / High / Ultra) for
 *             one-click fidelity. Below them: render-scale slider +
 *             individual toggles (shadows, bloom, tilt-shift,
 *             vignette, camera shake, seasonal weather, fireflies,
 *             FPS chip). Toggling any individual flag flips the
 *             preset chip to "Custom".
 *
 *   SAVE      Save now / Reset farm. Save is auto-applied every event
 *             so this is mostly for explicit milestone saves and the
 *             nuclear option.
 *
 * Distinct from every other panel in two ways:
 *
 *   1. Centered modal — positioned in the middle of the viewport
 *      instead of pinned to the top-right side dock, with a blurred
 *      backdrop dimming the world behind it.
 *   2. Pauses the simulation — `sim.paused = true` for the duration
 *      so the day doesn't advance, crops don't grow, the truck
 *      doesn't drive while the player is configuring things.
 *
 * The Controls section has been split out into its own static HUD
 * card (always visible under the InfoCard) — having keybinds always
 * on-screen is more useful than buried behind a modal.
 *
 * All values persist via {@link Settings} (separate localStorage key
 * from the game save — settings survive "Reset farm").
 */
export class SettingsPanel extends Panel {
  /** Full-screen blurred backdrop. Owned by this panel so its
   *  lifecycle (show/hide) stays atomic with the modal. Click on
   *  the backdrop closes the modal — standard modal UX. */
  private backdrop: HTMLDivElement;

  constructor(sim: Sim, private settings: Settings, private save: Save) {
    super(sim);
    // Re-render on any settings change so toggled states reflect immediately.
    this.settings.subscribe(() => {
      this.root.dataset.bumpHash = String(Math.random());
    });
    // Slider drags don't fire click — wire a separate input listener.
    this.root.addEventListener('input', this.onInput);
    // Modal styling — controls positioning + the wider max-width that
    // makes the multi-section content readable.
    this.root.classList.add('ui-panel--modal');

    // Backdrop element — created once and toggled on show/hide. Mounted
    // to document.body so its position:fixed isn't constrained by any
    // ancestor's transform / containing block.
    this.backdrop = document.createElement('div');
    this.backdrop.className = 'ui-modal-backdrop';
    this.backdrop.style.display = 'none';
    this.backdrop.addEventListener('click', () => this.hide());
    document.body.appendChild(this.backdrop);
  }

  /** Override mount() to ignore the panel-host parent. The panel host
   *  lives in its own stacking context at z-index 20, which traps any
   *  child element underneath the modal backdrop (z 200) no matter
   *  how high the child's own z-index is. By forwarding to document.body,
   *  the modal and the backdrop become sibling root-level positioned
   *  elements whose z-indexes compete fairly. */
  mount(_parent: HTMLElement) {
    super.mount(document.body);
  }

  id() { return 'settings'; }
  title() { return 'Settings'; }
  hashInputs() {
    const s = this.settings;
    return [
      s.getMasterVolume().toFixed(3),
      s.isMuted() ? '1' : '0',
      s.showFps() ? '1' : '0',
      s.seasonalFx() ? '1' : '0',
      s.fireflies() ? '1' : '0',
      s.shadows() ? '1' : '0',
      s.bloom() ? '1' : '0',
      s.tiltShift() ? '1' : '0',
      s.vignette() ? '1' : '0',
      s.cameraShake() ? '1' : '0',
      s.renderScale().toFixed(2),
      s.graphicsPreset(),
      this.root.dataset.bumpHash ?? '',
    ].join('|');
  }

  /** Override show/hide to manage the backdrop + sim pause atomically
   *  with the panel's visibility. */
  show() {
    super.show();
    this.backdrop.style.display = '';
    this.sim.paused = true;
  }
  hide() {
    super.hide();
    this.backdrop.style.display = 'none';
    this.sim.paused = false;
  }

  body() {
    const s = this.settings;
    const vol = Math.round(s.getMasterVolume() * 100);
    const muted = s.isMuted();
    const preset = s.graphicsPreset();
    const rs = s.renderScale();
    return `
      <div class="ui-settings__paused">⏸ Game paused</div>

      ${this.section('Sound', `
        <div class="ui-settings__row">
          <label for="setting-volume">Master volume</label>
          <input id="setting-volume" type="range" min="0" max="100" step="1"
                 value="${vol}" data-act="volume"
                 class="ui-settings__slider"
                 ${muted ? 'disabled' : ''}>
          <span class="ui-settings__value">${muted ? 'muted' : `${vol}%`}</span>
        </div>
        ${this.toggle('mute', 'Mute all sound', muted)}
        <div class="ui-tip">Volume affects gameplay sounds, ambient bed (forest, wind, crickets) and positional emitters (truck motor).</div>
      `)}

      ${this.section('Graphics', `
        <div class="ui-settings__presets">
          ${this.presetChip('low',    'Low',    preset)}
          ${this.presetChip('medium', 'Medium', preset)}
          ${this.presetChip('high',   'High',   preset)}
          ${this.presetChip('ultra',  'Ultra',  preset)}
          ${preset === 'custom' ? `<span class="ui-settings__preset-chip custom" title="Individual toggles diverge from any preset">Custom</span>` : ''}
        </div>
        <div class="ui-settings__row">
          <label for="setting-render-scale">Render scale</label>
          <input id="setting-render-scale" type="range" min="50" max="200" step="5"
                 value="${Math.round(rs * 100)}" data-act="renderScale"
                 class="ui-settings__slider">
          <span class="ui-settings__value">${rs.toFixed(2)}×</span>
        </div>
        ${this.toggle('shadows',     'Sun shadows',                  s.shadows())}
        ${this.toggle('bloom',       'Bloom (golden-hour glow)',     s.bloom())}
        ${this.toggle('tiltShift',   'Tilt-shift miniature blur',    s.tiltShift())}
        ${this.toggle('vignette',    'Vignette',                     s.vignette())}
        ${this.toggle('cameraShake', 'Camera shake on impacts',      s.cameraShake())}
        ${this.toggle('seasonalFx',  'Seasonal weather particles',   s.seasonalFx())}
        ${this.toggle('fireflies',   'Fireflies at night',           s.fireflies())}
        ${this.toggle('showFps',     'FPS / performance chip',       s.showFps())}
        <div class="ui-tip">Render scale &lt; 1.0 trades sharpness for speed (often the biggest perf win). Disable shadows + bloom on weak GPUs.</div>
      `)}

      ${this.section('Save', `
        <div class="ui-row"><span>Auto-save</span><strong>on every action</strong></div>
        <button class="ui-btn ui-btn--primary ui-btn--block" data-act="save-now">Save now</button>
        <button class="ui-btn ui-btn--danger ui-btn--block" data-act="reset-farm">Reset farm</button>
        <div class="ui-tip">Reset clears game progress and regenerates the forest. Settings are kept.</div>
      `)}

      <button class="ui-btn ui-btn--block" data-act="reset-settings">Reset settings to defaults</button>
    `;
  }

  protected onClick(e: Event) {
    const t = e.target as HTMLElement;
    const actEl = t.closest('[data-act]') as HTMLElement | null;
    if (!actEl) return;
    const act = actEl.dataset.act;

    if (act === 'preset') {
      const name = actEl.dataset.preset as Exclude<GraphicsPreset, 'custom'> | undefined;
      if (name === 'low' || name === 'medium' || name === 'high' || name === 'ultra') {
        this.settings.applyGraphicsPreset(name);
      }
      return;
    }

    switch (act) {
      case 'mute':         this.settings.toggleMute(); break;
      case 'showFps':      this.settings.setShowFps(!this.settings.showFps()); break;
      case 'seasonalFx':   this.settings.setSeasonalFx(!this.settings.seasonalFx()); break;
      case 'fireflies':    this.settings.setFireflies(!this.settings.fireflies()); break;
      case 'shadows':      this.settings.setShadows(!this.settings.shadows()); break;
      case 'bloom':        this.settings.setBloom(!this.settings.bloom()); break;
      case 'tiltShift':    this.settings.setTiltShift(!this.settings.tiltShift()); break;
      case 'vignette':     this.settings.setVignette(!this.settings.vignette()); break;
      case 'cameraShake':  this.settings.setCameraShake(!this.settings.cameraShake()); break;
      case 'save-now':
        this.save.write(this.sim.state);
        this.sim.bus.emit({ type: 'hint', text: 'Saved.' });
        break;
      case 'reset-farm':
        if (confirm('Reset the farm? This wipes game progress (settings are kept).')) {
          this.save.clear();
          location.reload();
        }
        break;
      case 'reset-settings':
        if (confirm('Reset every setting to its default?')) this.settings.resetToDefaults();
        break;
    }
  }

  /** Slider drag handler — type="range" fires 'input' not 'click'. */
  private onInput = (e: Event) => {
    const t = e.target as HTMLInputElement;
    if (!(t instanceof HTMLInputElement)) return;
    if (t.dataset.act === 'volume') {
      this.settings.setMasterVolume(clamp01(Number(t.value) / 100));
    } else if (t.dataset.act === 'renderScale') {
      this.settings.setRenderScale(Number(t.value) / 100);
    }
  };

  private section(title: string, inner: string): string {
    return `
      <div class="ui-settings__section">
        <div class="ui-settings__section-title">${title}</div>
        ${inner}
      </div>
    `;
  }

  private toggle(act: string, label: string, on: boolean): string {
    return `
      <button class="ui-settings__toggle ${on ? 'on' : 'off'}" data-act="${act}" aria-pressed="${on}">
        <span class="ui-settings__toggle-pill"><span class="ui-settings__toggle-knob"></span></span>
        <span class="ui-settings__toggle-label">${label}</span>
        <span class="ui-settings__toggle-state">${on ? 'On' : 'Off'}</span>
      </button>
    `;
  }

  private presetChip(name: Exclude<GraphicsPreset, 'custom'>, label: string, currentPreset: GraphicsPreset): string {
    const active = currentPreset === name;
    return `<button class="ui-settings__preset-chip ${active ? 'active' : ''}"
                    data-act="preset" data-preset="${name}"
                    aria-pressed="${active}">${label}</button>`;
  }
}

function clamp01(v: number): number {
  return v < 0 ? 0 : v > 1 ? 1 : v;
}
